import { useEffect, useState } from "react";
import { RESPONSE_STATUS } from "../utils/constants";
import {
  API_GET_DASHBOARDS,
  API_GET_SMARTVIEW_CONFIG,
} from "../network/service";
import { initializeDashboardsCache } from "./helpers";
import { cssHost } from "../utils/config";
import { setUserDetails } from "../utils/authUtils";

export const fetchUserData = async (
  setDashboards,
  setActiveDashboard,
  processAndStoreData
) => {
  setDashboards({ responseStatus: RESPONSE_STATUS.requestPending });
  try {
    const response = await API_GET_DASHBOARDS();
    if (response.status === 200) {
      const userData = response.data;
      if (userData) {
        initializeDashboardsCache(userData);
        setDashboards({
          response: userData,
          responseStatus: RESPONSE_STATUS.requestFullfilled,
        });
        setActiveDashboard(userData[0]);
        processAndStoreData(userData);
      }
    } else {
      setDashboards({ responseStatus: RESPONSE_STATUS.requestRejected });
      console.error(`Request failed with status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error fetching user data:", error);
    setDashboards({ responseStatus: RESPONSE_STATUS.requestRejected });
  }
};

export const fetchSmartViewsConfig = async (setSmartviewsConfig) => {
  setSmartviewsConfig({ responseStatus: RESPONSE_STATUS.requestPending });
  try {
    const response = await API_GET_SMARTVIEW_CONFIG();
    if (response.status === 200) {
      const smartviewdata = response.data;
      if (smartviewdata) {
        setSmartviewsConfig({
          response: smartviewdata,
          responseStatus: RESPONSE_STATUS.requestFullfilled,
        });
      }
    } else {
      setSmartviewsConfig({
        responseStatus: RESPONSE_STATUS.requestRejected,
      });
      console.error(`Request failed with status: ${response.status}`);
    }
  } catch (error) {
    console.error("Error fetching smartviews configuration:", error);
    setSmartviewsConfig({ responseStatus: RESPONSE_STATUS.requestRejected });
  }
};

export const initialize = async (
  setSmartviewsConfig,
  setDashboards,
  setActiveDashboard,
  processAndStoreData,
  setCssLoad
) => {
  injectMainCss(setCssLoad);
  await Promise.all([
    fetchSmartViewsConfig(setSmartviewsConfig),
    fetchUserData(setDashboards, setActiveDashboard, processAndStoreData),
  ]);
};

export const processAndStoreData = (dashboards) => {
  const result = {};
  dashboards.forEach((dashboard) => {
    const dashboardId = dashboard.id;
    const dashboardName = dashboard.name;
    dashboard?.layout?.widgets?.forEach((widget) => {
      const widgetId = widget.id;
      const widgetName = widget?.config?.name;
      const widgetConfig = widget.config;
      const status = widgetConfig?.taskStatus
        ? {
            value: widgetConfig?.taskStatus?.id,
            label: widgetConfig?.taskStatus?.name,
          }
        : null;
      const TaskType = widgetConfig?.taskTypes;
      const DueDate = widgetConfig?.dateFilters
        ? {
            value: widgetConfig.dateFilters.id,
            label: widgetConfig.dateFilters.name,
          }
        : null;

      const widgetFilters = {
        dashboardId,
        dashboardName,
        widgetId,
        widgetName,
        filters: {
          DueDate,
          status,
          TaskType,
        },
      };
      if (Object.keys(widgetFilters.filters).length > 0) {
        result[widgetId] = widgetFilters;
      }
    });
  });

  localStorage.setItem("casa-web-filters", JSON.stringify(result));
};
export const injectMainCss = (setCssLoad) => {
  setCssLoad({ responseStatus: RESPONSE_STATUS.requestPending });
  // The new (Vite) build loads its own hashed CSS via the casadashboardentry
  // loader, which sets __CASA_BUNDLED_CSS__ — so skip injecting the legacy
  // fixed-name entry.css there. The legacy build leaves the flag unset and
  // injects entry.css exactly as before.
  if (typeof window !== "undefined" && window.__CASA_BUNDLED_CSS__) {
    setCssLoad({ responseStatus: RESPONSE_STATUS.requestFullfilled });
    return;
  }
  try {
    const link2 = document.createElement("link");
    link2.href = `${cssHost}/entry.css`;
    link2.rel = "stylesheet";
    document.head.appendChild(link2);
    setCssLoad({ responseStatus: RESPONSE_STATUS.requestFullfilled });
  } catch (error) {
    setCssLoad({ responseStatus: RESPONSE_STATUS.requestRejected });
  }
};

const useInitializeDashboard = () => {
  const [layout, setLayout] = useState([]);
  const [dashboards, setDashboards] = useState({
    responseStatus: RESPONSE_STATUS.requestPending,
  });
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [cssLoad, setCssLoad] = useState({
    responseStatus: RESPONSE_STATUS.requestPending,
  });
  const [smartviewsConfig, setSmartviewsConfig] = useState({
    responseStatus: RESPONSE_STATUS.requestPending,
  });

  useEffect(() => {
    setUserDetails();
    initialize(
      setSmartviewsConfig,
      setDashboards,
      setActiveDashboard,
      processAndStoreData,
      setCssLoad
    );
  }, []);

  useEffect(() => {
    const fetchDashboardWidgets = async () => {
      if (activeDashboard?.layout?.widgets?.length > 0) {
        const widgetLayout = activeDashboard.layout.widgets;
        setLayout([...widgetLayout]);
      } else {
        setLayout([]); // Reset layout if no widgets are present
      }
    };

    if (activeDashboard) {
      fetchDashboardWidgets();
    } else {
      setLayout([]); // Reset layout if no active dashboard is selected
    }
  }, [activeDashboard]);

  return {
    layout,
    dashboards,
    activeDashboard,
    cssLoad,
    setActiveDashboard,
    smartviewsConfig,
    initialize: () =>
      initialize(
        setSmartviewsConfig,
        setDashboards,
        setActiveDashboard,
        processAndStoreData,
        setCssLoad
      ),
  };
};

export default useInitializeDashboard;
